import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  CompanyGroupProductOnboardingRow,
  TeamCompaniesService,
} from '../../../services/teamcompanies/teamcompanies.service';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../modal/confirmdialog/confirmdialog.component';
import { SnackBarComponent } from '../../snackbar/snackbar.component';

export interface GroupProductOnboardingStatusDialogData {
  companyId: string;
  companyName: string;
}

@Component({
    selector: 'app-group-product-onboarding-status-dialog',
    templateUrl: './group-product-onboarding-status-dialog.component.html',
    styleUrls: ['./group-product-onboarding-status-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class GroupProductOnboardingStatusDialogComponent implements OnInit {
  protected isLoading = true;
  protected rows: CompanyGroupProductOnboardingRow[] = [];
  protected pendingProductIds = new Set<string>();

  constructor(
    @Inject(MAT_DIALOG_DATA) protected data: GroupProductOnboardingStatusDialogData,
    private dialogRef: MatDialogRef<GroupProductOnboardingStatusDialogComponent, boolean>,
    private dialog: MatDialog,
    private teamCompaniesService: TeamCompaniesService,
    private snackBar: MatSnackBar,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.reload();
  }

  protected close(updated = false): void {
    this.dialogRef.close(updated);
  }

  protected async markReady(row: CompanyGroupProductOnboardingRow): Promise<void> {
    const vendorProductId = `${row?.requestedVendorProductId || ''}`.trim();
    if (!this.data?.companyId || !vendorProductId || this.pendingProductIds.has(vendorProductId)) {
      return;
    }

    this.pendingProductIds.add(vendorProductId);
    try {
      await this.teamCompaniesService.markGroupProductOnboardingReady(this.data.companyId, vendorProductId);
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: `${row.providerName} - ${row.planName} marked ready.`,
      });
      await this.reload();
    } catch (error: any) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: error?.error?.error || 'Unable to mark this product approval ready.',
      });
    } finally {
      this.pendingProductIds.delete(vendorProductId);
    }
  }

  protected getStatusLabel(row: CompanyGroupProductOnboardingRow): string {
    if (row?.status === 'ready') {
      return 'Ready';
    }
    if (row?.status === 'pending') {
      return 'Awaiting Wallit Confirmation';
    }
    return 'Not Requested';
  }

  protected canMarkReady(row: CompanyGroupProductOnboardingRow): boolean {
    return row?.status === 'pending';
  }

  protected canDelete(row: CompanyGroupProductOnboardingRow): boolean {
    return row?.status === 'pending' || row?.status === 'ready';
  }

  protected async deleteRequest(row: CompanyGroupProductOnboardingRow): Promise<void> {
    const vendorProductId = `${row?.requestedVendorProductId || ''}`.trim();
    if (!this.data?.companyId || !vendorProductId || this.pendingProductIds.has(vendorProductId)) {
      return;
    }

    const dialogRef = this.dialog.open<ConfirmDialogComponent, ConfirmDialogModel, boolean>(ConfirmDialogComponent, {
      data: new ConfirmDialogModel(
        'Delete Product Approval Request',
        this.getDeleteConfirmationCopy(row),
        false,
        'Delete Request',
        'Cancel',
      ),
      autoFocus: false,
    });

    const confirmed = await dialogRef.afterClosed().toPromise();
    if (!confirmed) {
      return;
    }

    this.pendingProductIds.add(vendorProductId);
    try {
      await this.teamCompaniesService.deleteGroupProductOnboardingRequest(this.data.companyId, vendorProductId);
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: `${row.providerName} - ${row.planName} request deleted.`,
      });
      await this.reload();
    } catch (error: any) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: error?.error?.error || 'Unable to delete this product approval request.',
      });
    } finally {
      this.pendingProductIds.delete(vendorProductId);
    }
  }

  protected isUpdating(row: CompanyGroupProductOnboardingRow): boolean {
    return this.pendingProductIds.has(row?.requestedVendorProductId);
  }

  private getDeleteConfirmationCopy(row: CompanyGroupProductOnboardingRow): string {
    const productLabel = `${row?.providerName || 'This provider'} - ${row?.planName || 'this plan'}`.trim();
    if (row?.status === 'ready') {
      return `Delete the approval record for <strong>${productLabel}</strong>? This will make the plan require approval again for Payroll Admin companies.`;
    }
    return `Delete the approval request for <strong>${productLabel}</strong>?`;
  }

  private async reload(): Promise<void> {
    this.isLoading = true;
    try {
      this.rows = await this.teamCompaniesService.getGroupProductOnboardingStatuses(this.data.companyId);
    } finally {
      this.isLoading = false;
    }
  }
}
